// @vendors
import { useMemo, useState } from "react";
import {Form, Formik} from "formik";
import { useDispatch } from "react-redux";

// @state
import {useUI} from "../../../../app/context/ui";

// @assets
import {CreateEmployeeStyles} from "../../../../assets/css/createEmployee-style";
import { theme } from "../../../../assets/theme";

// @components
import {
  AdapterDateFns,
  Box,
  Button,
  Container,
  LocalizationProvider,
  TextField,
  Typography,
} from "../../../../components/shared/MaterialUI";

// @helpers
import AppHelper from "../../../../helpers/AppHelper";
import {validationEmployee} from '../helpers/validationEmployee';
import { formatPhoneNumber } from "../../../../helpers/FormatPhoneNumber";

// @services
import EmployeeCompanyServiceNewApi from "../../../../services/newApi/EmployeeCompanyServiceNewApi";
import HelpersServiceNewApi from "../../../../services/newApi/HelpersServiceNewApi";

// @constants
import { types } from "../../../../redux/types";

const employeeValue = {
  firstName: "",
  lastName: "",
  email: "",
  externalEmployeeId: "",
  phoneNumber: ""
}

const CreateEmployee = (props) => {
  const URL = window.location.origin;
  const {dialogUI, blockUI, snackbarUI} = useUI();
  const styles = useMemo(() => CreateEmployeeStyles(), []);
  const employeeServiceNewApi = useMemo(() => new EmployeeCompanyServiceNewApi(), []);
  const helperServiceNewApi = useMemo(() => new HelpersServiceNewApi(), []);
  const {token, isRyze=false} = props;
  const dispatch = useDispatch();
  const [formValues, setFormValues] = useState(employeeValue);

  const settings = {
    confirm: true,
    btn: {
      confirm: "Close",
      close: "",
    },
    onConfirm: () => {
      dialogUI.current.close();
    },
    styles: {...styles.dialog},
  };

  const handleChangeValue = (e) => {
    setFormValues({...formValues, [e.target.name]: e.target.value });
  }

  const handlePhoneChange = (e) => {
      const formattedPhoneNumber = formatPhoneNumber(e.target.value);
      setFormValues({...formValues, [e.target.name]: formattedPhoneNumber });
  };

  const onSubmit = () => {
    const values = formValues;
    try {
      blockUI.current.open(true);
      dataToSend(values)
    } catch (e) {
      blockUI.current.open(false);
      AppHelper.checkError(e, snackbarUI);
    }
  };

  const dataToSend = async (values) => {
    dispatch({
      type: types.ID_EMPLOYEE_SELECTED,
      detail: ''
    })
    if (token) {
      return sendEmployeeExternal(values)
    } else {
      return sendEmployeeInternal(values)
    }
  }

  const sendEmployeeExternal = async (values) => {
    try {
      const data = valuesToSendExternal(values);
      const res = await employeeServiceNewApi.addEmployeeExternal(data, token, isRyze );
      const link = res?.data?.data[0];
      if(link?.link)return window.location.href = link?.link;
      blockUI.current.open(false);
    } catch (e) {
      blockUI.current.open(false);
      if(e?.response?.data?.errors[0]?.includes("'email' already exists"))return navigateToLink(e?.response?.data?.data, "?origin=qr");
      AppHelper.checkError(e, snackbarUI);
    }
  }
  
  const sendEmployeeInternal = async (values) => {
    try {
      const data = valuesToSendInternal(values)
      await employeeServiceNewApi.addEmployee(data, props?.companyId)
      props.getEmployees();
      openModalAccountCreated()
      blockUI.current.open(false);
    } catch (e) {
      blockUI.current.open(false);
      const objectValues = {
        companyId: props?.companyId,  
        email: values.email
      }
      if(e?.response?.data?.errors[0]?.includes("'email' already exists"))return emailAlreadyExists(objectValues);
      AppHelper.checkError(e, snackbarUI);
    }
  }

  const emailAlreadyExists = async (objectValues) => {
    //snackbarUI.current.open(`${error},\n The questionnaire link was resent to your email.`);
    try {
      const response = await helperServiceNewApi.getLinkSurvey(objectValues);
      if(response?.data?.data && typeof(response?.data?.data) === 'string') return navigateToLink(response?.data?.data);
    } catch (error) {
       blockUI.current.open(false);
      AppHelper.checkError(error, snackbarUI);
    }
  }

  const navigateToLink = (hash, origin) => {
    if(!hash)return;
    const mainRoute = origin ? `${URL}/quest/${hash}${origin}` :  `${URL}/quest/${hash}`;
    return window.location.href = mainRoute;
  }

  const valuesToSendExternal = (values) => {
    const DATA = {
      phone_number: values.phoneNumber ?? null,
      first_name: values?.firstName,
      middle_name: null,
      last_name: values?.lastName,
      email: values?.email,
      address: null,
      state: null,
      city: null,
      county: null,
      zip_code: null,
      social_number: null,
      date_of_birth: null,
      occupation_code: null,
      date_gave_info: null,
      date_was_offered_job: null,
      date_was_hired: null,
      date_started_job: null,
      hourly_start_wage: 0,
      job_position: null,
      subcription: true,
      is_manual: false,
      summer_youth_emp_zone: false,
      external_employeeId: values.externalEmployeeId ? values.externalEmployeeId : null,
      state_certification: null
    }
    return DATA
  }

  const valuesToSendInternal = (values) => {
    const DATA = {
      phoneNumber: values.phoneNumber ?? null,
      firstName: values?.firstName,
      middleName: null,
      lastName: values?.lastName,
      email: values?.email,
      address: null,
      state: null,
      city: null,
      county: null,
      zipCode: null,
      socialNumber: null,
      dateOfBirth: null,
      occupationCode: null,
      dateGaveInfo: null,
      dateWasOfferedJob: null,
      dateWasHired: null,
      dateStartedJob: null,
      hourlyStartWage: 0,
      jobPosition: null,
      isManual: false,
      summerYouthEmpZone: false,
      stateCertification: null,
      externalEmployeeId: values.externalEmployeeId ? values.externalEmployeeId : null
    }
    return DATA
  }

  const openModalAccountCreated = () => {
    setTimeout(() => {
      setFormValues(employeeValue);
      blockUI.current.open(false);
      dialogUI.current.open(
        "Account created!",
        "The account has been created successfully. \n An email with the link to the questionnaire has been sent.",
        settings
      );
    }, 500);
  }

  const isShowExternalId = () => {
    if(isRyze)return false;
    if(props?.hideExternalId)return false;
    return true;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container
        component="div"
        maxWidth={false}
        sx={styles.container}
      >
        {!isRyze &&
          <Typography variant="h3" sx={{ color: theme.palette.primary.black }}>
            Create new employee
          </Typography>
        }
        <Formik
          initialValues={{...formValues}}
          validationSchema={validationEmployee}
          onSubmit={() => {
            onSubmit();
          }}
          enableReinitialize={true}
        >
          {(props) => {
            const { touched, errors, handleBlur } = props;
            return (
              <Form autoComplete="off" className="formCreateUser">
                <Box sx={styles.inputsCtn}>
                  <Box sx={styles.boxItem}>
                    <p style={{ color: theme.palette.secondary.black }} className="labelInput">
                      First name: <span style={{color: "red"}}>*</span>
                    </p>
                    <TextField
                      sx={styles.input}
                      label="Type your first name here"
                      variant="outlined"
                      id="firstName"
                      name="firstName"
                      value={formValues.firstName || ''}
                      onChange={(e) => handleChangeValue(e)}
                      onBlur={handleBlur}
                      error={touched.firstName && Boolean(errors.firstName) }
                      helperText={
                        errors.firstName && touched.firstName
                          ? errors.firstName
                          : ''
                      }
                    />
                  </Box>

                  <Box sx={styles.boxItem}>
                    <p style={{ color: theme.palette.secondary.black }} className="labelInput">
                      Last name: <span style={{color: "red"}}>*</span>
                    </p>
                    <TextField
                      sx={styles.input}
                      label="Type your last name here"
                      variant="outlined"
                      id="lastName"
                      name="lastName"
                      value={formValues.lastName || ''}
                      onChange={(e) => handleChangeValue(e)}
                      onBlur={handleBlur}
                      error={touched.lastName && Boolean(errors.lastName) }
                      helperText={
                        errors.lastName && touched.lastName
                          ? errors.lastName
                          : ''
                      }
                    />
                  </Box>

                  <Box sx={styles.boxItem}>
                    <p style={{ color: theme.palette.secondary.black }} className="labelInput">
                      Email: <span style={{color: "red"}}>*</span>
                    </p>
                    <TextField
                      sx={styles.input}
                      label="example@email.com"
                      variant="outlined"
                      autoComplete="new-password"
                      id="email"
                      name="email"
                      type="email"
                      value={formValues.email || ''}
                      onChange={(e) => handleChangeValue(e)}
                      onBlur={handleBlur}
                      error={touched.email && Boolean(errors.email) }
                      helperText={
                        errors.email && touched.email
                          ? errors.email
                          : ''
                      }
                    />
                  </Box>

                  <Box sx={styles.boxItem}>
                    <p style={{ color: theme.palette.secondary.black }} className="labelInput">
                      Phone number:
                    </p>
                    <TextField
                      sx={styles.input}
                      label="Type your phone number here"
                      placeholder="(555) 555-5555"
                      variant="outlined"
                      id="phoneNumber"
                      name="phoneNumber"
                      value={formValues.phoneNumber || ''}
                      onChange={(phone) => handlePhoneChange(phone)}
                      onBlur={handleBlur}
                      error={touched.phoneNumber && Boolean(errors.phoneNumber) }
                      helperText={
                        errors.phoneNumber && touched.phoneNumber
                          ? errors.phoneNumber
                          : ''
                      }
                    />
                  </Box>

                  {isShowExternalId() &&
                    <Box sx={styles.boxItem}>
                      <p style={{ color: theme.palette.secondary.black }} className="labelInput">
                        External employee id:
                      </p>
                      <TextField
                        sx={styles.input}
                        label="External employee id"
                        variant="outlined"
                        id="externalEmployeeId"
                        name="externalEmployeeId"
                        value={formValues.externalEmployeeId || ''}
                        onChange={(e) => handleChangeValue(e)}
                        onBlur={handleBlur}
                        error={touched.externalEmployeeId && Boolean(errors.externalEmployeeId) }
                        helperText={
                          errors.externalEmployeeId && touched.externalEmployeeId
                            ? errors.externalEmployeeId
                            : ''
                        }
                      />
                    </Box>
                  }
                </Box>

                <Box sx={styles.formButtonWrapper}>
                  {!isRyze &&
                    <Button
                      color="inherit"
                      onClick={() => {
                        dialogUI.current.close();
                      }}
                    >
                      Cancel
                    </Button>
                  }
                  <Button
                    type="submit"
                    sx={{
                      backgroundColor: theme.palette.primary.black,
                      color: theme.palette.primary.white,
                      '&:hover': {
                        backgroundColor: theme.palette.primary.black,
                      },
                    }}
                  >
                    {!isRyze ? 'Create' : 'Next'}
                  </Button>
                </Box>
              </Form>
            );
          }}
        </Formik>
      </Container>
    </LocalizationProvider>
  );
};

export default CreateEmployee;
